import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { giamSatThiService } from 'services/giamSatThiService';
import './giamSatThi.scss';

/* ─── Hằng số ─────────────────────────────────────── */
const POLL_LIST_MS = 5000;  // Polling danh sách thí sinh
const POLL_SKILLS_MS = 5000;  // Polling kỹ năng học viên đang chọn

const SKILL_ORDER = ['NGUPHAP', 'NGHE', 'DOC', 'VIET', 'NOI'];
const SKILL_LABELS = { NGUPHAP: 'Ngữ pháp', NGHE: 'Nghe', DOC: 'Đọc', VIET: 'Viết', NOI: 'Nói' };

const STATUS_BADGE = {
    warning: { cls: 'warning', icon: 'fa-triangle-exclamation', text: 'Cảnh báo' },
    joined: { cls: 'joined', icon: null, text: 'Đang thi' },
    submitted: { cls: 'submitted', icon: 'fa-check', text: 'Đã nộp' },
    'not-joined': { cls: 'not-joined', icon: null, text: 'Chưa vào' },
    danger: { cls: 'danger', icon: 'fa-ban', text: 'Đình chỉ' },
};

const SKILL_STATUS_MAP = {
    pending: { cls: 'pending', text: 'Chưa vào' },
    active: { cls: 'active', text: 'Đang làm' },
    completed: { cls: 'completed', text: 'Hoàn thành' },
};

/* ─── Component ────────────────────────────────────── */
const GiamSatThi = () => {
    const navigate = useNavigate();

    /* Thông tin đăng nhập giám thị từ localStorage */
    const fullname = localStorage.getItem('giam_thi_fullname') || 'Giám thị';

    /* ── Ca thi từ login (cố định, không thể đổi) ── */
    const caThiId = Number(localStorage.getItem('giam_thi_ca_thi_id') || 0);
    const caThiInfoRaw = localStorage.getItem('giam_thi_ca_thi_info');
    const caThiInfo = caThiInfoRaw ? JSON.parse(caThiInfoRaw) : null;

    /* ── Phòng thi state (giám thị chọn phòng sau khi đã vào ca thi) ── */
    const [listPhongThi, setListPhongThi] = useState([]);
    const [phongThiId, setPhongThiId] = useState(0); // 0 = tất cả
    const [loadingPhongThi, setLoadingPhongThi] = useState(true);

    /* ── State ── */
    const [overview, setOverview] = useState(null);
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [skills, setSkills] = useState({});
    const [logs, setLogs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showLockModal, setShowLockModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [loadingSkills, setLoadingSkills] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionMsg, setActionMsg] = useState('');
    const [lastUpdated, setLastUpdated] = useState('');

    const [lockData, setLockData] = useState({ reason: '', message: 'Bạn đã bị đình chỉ thi do vi phạm quy chế.' });
    const [reportData, setReportData] = useState({ violationType: 'eye', notes: '' });

    /* Refs cho timers */
    const listTimerRef = useRef(null);
    const skillsTimerRef = useRef(null);

    /* ── Nếu chưa có caThiId → redirect về login ── */
    useEffect(() => {
        if (!caThiId) {
            navigate('/giam-sat-thi/login');
        }
    }, [caThiId, navigate]);

    /* ── Logout ── */
    const handleLogout = () => {
        ['giam_thi_token', 'giam_thi_user_id', 'giam_thi_fullname', 'giam_thi_ca_thi_id', 'giam_thi_ca_thi_info']
            .forEach(k => localStorage.removeItem(k));
        navigate('/giam-sat-thi/login');
    };

    /* ── Fetch danh sách phòng thi của ca thi ── */
    useEffect(() => {
        if (!caThiId) return;
        const fetchPhongThi = async () => {
            setLoadingPhongThi(true);
            try {
                const res = await giamSatThiService.getPhongThi(caThiId);
                if (res?.data?.status === 'success') {
                    setListPhongThi(res.data.data || []);
                }
            } catch { /* silent */ } finally {
                setLoadingPhongThi(false);
            }
        };
        fetchPhongThi();
    }, [caThiId]);

    const handleChangePhongThi = (e) => {
        const id = Number(e.target.value);
        setPhongThiId(id);
        setSelectedStudent(null);
    };

    /* ── Fetch tổng quan ── */
    const fetchOverview = useCallback(async () => {
        if (!caThiId) return;
        try {
            const res = await giamSatThiService.getOverview(caThiId);
            if (res?.data?.status === 'success') {
                setOverview(res.data.data);
            }
        } catch { /* silent */ }
    }, [caThiId]);

    /* ── Fetch danh sách thí sinh ── */
    const fetchStudents = useCallback(async () => {
        if (!caThiId) return;
        try {
            const res = await giamSatThiService.getListStudents(caThiId, phongThiId || undefined);
            if (res?.data?.status === 'success') {
                setStudents(res.data.data || []);
                if (res.data.stats) {
                    setOverview(prev => prev ? { ...prev, stats: res.data.stats } : { stats: res.data.stats });
                }
                setLastUpdated(new Date().toLocaleTimeString('vi-VN'));
            }
        } catch { /* silent */ }
    }, [caThiId, phongThiId]);

    /* ── Fetch kỹ năng của thí sinh đang chọn ── */
    const fetchSkills = useCallback(async (userId) => {
        if (!caThiId || !userId) return;
        setLoadingSkills(true);
        try {
            const res = await giamSatThiService.getStudentSkills(caThiId, userId);
            if (res?.data?.status === 'success') {
                setSkills(res.data.skills || {});
            }
        } catch { /* silent */ } finally {
            setLoadingSkills(false);
        }
    }, [caThiId]);

    /* ── Fetch audit log ── */
    const fetchLogs = useCallback(async (userId) => {
        if (!caThiId || !userId) return;
        try {
            const res = await giamSatThiService.getStudentDetail(caThiId, userId);
            if (res?.data?.status === 'success') {
                setLogs(res.data.data || []);
            }
        } catch { /* silent */ }
    }, [caThiId]);

    /* ── Khởi động polling ── */
    useEffect(() => {
        if (!caThiId) return;
        fetchOverview();
        fetchStudents();
        listTimerRef.current = setInterval(fetchStudents, POLL_LIST_MS);
        return () => clearInterval(listTimerRef.current);
    }, [fetchStudents, fetchOverview, caThiId, phongThiId]);

    useEffect(() => {
        clearInterval(skillsTimerRef.current);
        if (!selectedStudent) return;

        fetchSkills(selectedStudent.user_id);
        fetchLogs(selectedStudent.user_id);
        skillsTimerRef.current = setInterval(() => fetchSkills(selectedStudent.user_id), POLL_SKILLS_MS);
        return () => clearInterval(skillsTimerRef.current);
    }, [selectedStudent?.user_id, fetchSkills, fetchLogs]);

    /* ── Xử lý chọn thí sinh ── */
    const handleSelectStudent = (student) => {
        setSelectedStudent(student);
        setSkills({});
        setLogs([]);
    };

    /* ── Xử lý action: lock / resume / report ── */
    const callAction = async (action, extra = {}) => {
        if (!selectedStudent) return;
        setActionLoading(true);
        setActionMsg('');
        try {
            const res = await giamSatThiService.action({
                caThiId,
                userId: selectedStudent.user_id,
                action,
                ...extra,
            });
            setActionMsg(res?.data?.message || 'Thực hiện thành công');
            fetchStudents();
        } catch (err) {
            setActionMsg(err?.response?.data?.message || 'Có lỗi xảy ra');
        } finally {
            setActionLoading(false);
        }
    };

    const handleLockStudent = async () => {
        await callAction('lock', { reason: lockData.reason });
        setShowLockModal(false);
    };

    const handleResume = async () => {
        await callAction('resume');
    };

    const handleSaveReport = async () => {
        await callAction('report', { reason: `${reportData.violationType}: ${reportData.notes}` });
        setShowReportModal(false);
    };

    /* ── Filter ── */
    const filteredStudents = students.filter(st => {
        const q = searchTerm.toLowerCase();
        const matchSearch = st.fullname?.toLowerCase().includes(q) || st.sbd?.toLowerCase().includes(q);
        if (filterStatus === 'all') return matchSearch;
        if (filterStatus === 'violations') return matchSearch && st.warnings > 0;
        if (filterStatus === 'submitted') return matchSearch && st.status === 'submitted';
        if (filterStatus === 'active') return matchSearch && st.status === 'joined';
        return matchSearch;
    });

    /* ── Badges ── */
    const getStatusBadge = (status) => {
        const badge = STATUS_BADGE[status] || STATUS_BADGE['not-joined'];
        return (
            <span className={`badge ${badge.cls}`}>
                {badge.icon && <i className={`fas ${badge.icon}`} />}
                {badge.text}
            </span>
        );
    };

    const getLogDot = (type) => {
        const map = { danger: '#ef4444', warning: '#f59e0b', success: '#10b981', info: '#3b82f6' };
        return map[type] || '#94a3b8';
    };

    /* ───────────────── RENDER ───────────────────────── */
    if (!caThiId) return null;

    return (
        <div className="giam-sat-thi">

            {/* ─── HEADER ─── */}
            <header className="exam-header">
                <div className="exam-title">
                    <i className="fa-solid fa-user-shield" />
                    <span>Giám sát thi</span>
                    {caThiInfo && (
                        <span className="ca-thi-badge">
                            {caThiInfo.name}
                            {caThiInfo.ky_thi_name ? ` — ${caThiInfo.ky_thi_name}` : ''}
                        </span>
                    )}
                </div>

                <div className="stats-bar">
                    {(caThiInfo?.thoi_gian_bat_dau || overview?.thoi_gian_bat_dau) && (
                        <div className="stat-item">
                            <i className="fa-regular fa-clock" />
                            <span className="timer">
                                {(caThiInfo?.thoi_gian_bat_dau || overview?.thoi_gian_bat_dau)?.substring(11, 16)} - {(caThiInfo?.thoi_gian_ket_thuc || overview?.thoi_gian_ket_thuc)?.substring(11, 16)}
                            </span>
                        </div>
                    )}
                    <div className="stat-item">
                        Tổng: <strong>{overview?.stats?.total ?? students.length}</strong>
                    </div>
                    <div className="stat-item stat-success">
                        Đã nộp: <strong>{overview?.stats?.submitted ?? 0}</strong>
                    </div>
                    <div className="stat-item stat-primary">
                        Đang thi: <strong>{overview?.stats?.active ?? 0}</strong>
                    </div>
                    <div className="stat-item stat-danger">
                        Cảnh báo: <strong>{overview?.stats?.warnings ?? 0}</strong>
                    </div>
                    {lastUpdated && (
                        <div className="stat-item stat-muted">
                            <i className="fa-solid fa-rotate" /> {lastUpdated}
                        </div>
                    )}
                </div>

                <div className="header-right">
                    <span className="supervisor-name">
                        <i className="fa-solid fa-user-tie" /> {fullname}
                    </span>
                    <button className="btn-logout" onClick={handleLogout} title="Đăng xuất">
                        <i className="fa-solid fa-right-from-bracket" />
                    </button>
                </div>
            </header>

            {/* ─── SELECTOR BAR: Phòng thi ─── */}
            <div className="selector-bar">
                <div className="selector-group">
                    <label><i className="fa-solid fa-door-open" /> Phòng thi:</label>
                    {loadingPhongThi ? (
                        <span className="loading-text"><i className="fa-solid fa-spinner fa-spin" /> Đang tải...</span>
                    ) : (
                        <select 
                            className="selector-dropdown" 
                            value={phongThiId} 
                            onChange={handleChangePhongThi}
                            disabled={listPhongThi.length === 0}
                        >
                            <option value={0}>
                                Tất cả phòng thi ({listPhongThi.reduce((s, p) => s + Number(p.thi_sinh_count || 0), 0)} thí sinh)
                            </option>
                            {listPhongThi.map(pt => (
                                <option key={pt.id} value={pt.id}>
                                    {pt.name || pt.code || `Phòng ${pt.id}`} — {pt.thi_sinh_count || 0} thí sinh
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="selector-info">
                    <span className="info-tag">
                        <i className="fa-solid fa-calendar-check" />
                        Ca thi #{caThiId}: {caThiInfo?.name || ''}
                    </span>
                    {listPhongThi.length > 0 && (
                        <span className="info-tag info-rooms">
                            <i className="fa-solid fa-door-open" />
                            {listPhongThi.length} phòng thi
                        </span>
                    )}
                </div>
            </div>

            {/* ─── MAIN ─── */}
            <div className="container">

                {/* ─── DANH SÁCH THÍ SINH ─── */}
                <div className="list-section">
                    <div className="filter-bar">
                        <div className="search-box">
                            <i className="fa-solid fa-search" />
                            <input
                                type="text"
                                placeholder="Tìm theo tên hoặc SBD..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        {['all', 'violations', 'submitted', 'active'].map(f => (
                            <button
                                key={f}
                                className={`filter-btn ${filterStatus === f ? 'active' : ''}`}
                                onClick={() => setFilterStatus(f)}
                            >
                                {f === 'all' && 'Tất cả'}
                                {f === 'violations' && <><i className="fa-solid fa-circle-exclamation" /> Vi phạm</>}
                                {f === 'submitted' && 'Đã nộp'}
                                {f === 'active' && 'Đang thi'}
                            </button>
                        ))}
                    </div>

                    {students.length === 0 ? (
                        <div className="loading-state">
                            <i className="fa-solid fa-spinner fa-spin" /> Đang tải danh sách...
                        </div>
                    ) : (
                        <table className="students-table">
                            <thead>
                                <tr>
                                    <th width="8%">SBD</th>
                                    <th width="22%">Họ tên</th>
                                    {!phongThiId && <th width="12%">Phòng thi</th>}
                                    <th width="12%">Trạng thái</th>
                                    <th width="18%">Tiến độ</th>
                                    <th width="8%">Vi phạm</th>
                                    <th width="20%">IP / Thiết bị</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.length === 0 ? (
                                    <tr><td colSpan={7} className="empty-row">Không có thí sinh nào.</td></tr>
                                ) : filteredStudents.map((st) => (
                                    <tr
                                        key={st.user_id}
                                        className={selectedStudent?.user_id === st.user_id ? 'selected' : ''}
                                        onClick={() => handleSelectStudent(st)}
                                        style={{ opacity: st.status === 'not-joined' ? 0.6 : 1 }}
                                    >
                                        <td><strong>{st.sbd}</strong></td>
                                        <td>{st.fullname}</td>
                                        {!phongThiId && (
                                            <td>
                                                {st.phong_thi_name ? (
                                                    <span className="room-tag">{st.phong_thi_name}</span>
                                                ) : <span className="no-data">--</span>}
                                            </td>
                                        )}
                                        <td>{getStatusBadge(st.status)}</td>
                                        <td>
                                            {st.status !== 'not-joined' ? (
                                                <>
                                                    <div className="progress-bar">
                                                        <div
                                                            className="progress-fill"
                                                            style={{ width: `${st.progressPercent || 0}%` }}
                                                        />
                                                    </div>
                                                    <div className="progress-text">{st.progressText}</div>
                                                </>
                                            ) : '--'}
                                        </td>
                                        <td>
                                            {st.warnings > 0
                                                ? <div className="warning-count"><i className="fa-solid fa-bell" /> {st.warnings} lỗi</div>
                                                : <span className="no-data">--</span>
                                            }
                                        </td>
                                        <td className="device-info">{st.ip}<br />{st.device}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* ─── DETAIL PANEL ─── */}
                <div className="detail-panel">
                    {selectedStudent ? (
                        <>
                            {/* Thông tin thí sinh */}
                            <div className="student-header">
                                <h2>{selectedStudent.fullname}</h2>
                                <div className="student-meta">
                                    <span>SBD: {selectedStudent.sbd}</span>
                                    <span>IP: {selectedStudent.ip}</span>
                                    {selectedStudent.phong_thi_name && (
                                        <span><i className="fa-solid fa-door-open" /> {selectedStudent.phong_thi_name}</span>
                                    )}
                                </div>
                                {getStatusBadge(selectedStudent.status)}
                            </div>

                            {/* Thông báo action */}
                            {actionMsg && (
                                <div className="action-result">
                                    <i className="fa-solid fa-circle-info" /> {actionMsg}
                                </div>
                            )}

                            {/* Các nút action */}
                            <div className="action-grid">
                                <button
                                    className="btn-action btn-lock"
                                    onClick={() => { setActionMsg(''); setShowLockModal(true); }}
                                    disabled={selectedStudent.is_locked == 1}
                                >
                                    <i className="fa-solid fa-lock" /> Khóa thi
                                </button>
                                <button
                                    className="btn-action btn-resume"
                                    onClick={handleResume}
                                    disabled={actionLoading || selectedStudent.is_locked != 1}
                                >
                                    <i className="fa-solid fa-play" />
                                    {actionLoading ? 'Đang xử lý...' : 'Mở lại'}
                                </button>
                                <button
                                    className="btn-action btn-report"
                                    onClick={() => { setActionMsg(''); setShowReportModal(true); }}
                                >
                                    <i className="fa-solid fa-file-pen" /> Lập biên bản
                                </button>
                                <button className="btn-action btn-submit" disabled>
                                    <i className="fa-solid fa-upload" /> Buộc nộp bài
                                </button>
                            </div>

                            {/* ─── BẢNG KỸ NĂNG (real-time Redis) ─── */}
                            <div className="skills-section">
                                <div className="skills-title">
                                    <span>
                                        <i className="fa-solid fa-chart-simple" /> Tiến độ từng kỹ năng
                                    </span>
                                    <span className="refresh-tag">
                                        {loadingSkills
                                            ? <><i className="fa-solid fa-spinner fa-spin" /> Đang tải...</>
                                            : <><i className="fa-solid fa-rotate" /> Real-time</>
                                        }
                                    </span>
                                </div>

                                <div className="skills-list">
                                    {SKILL_ORDER.map(key => {
                                        const s = skills[key];
                                        if (!s) {
                                            return (
                                                <div key={key} className="skill-row">
                                                    <span className="skill-name">{SKILL_LABELS[key]}</span>
                                                    <div className="skill-bar-wrap">
                                                        <div className="skill-bar-fill fill-pending" style={{ width: '0%' }} />
                                                    </div>
                                                    <span className="skill-count">--</span>
                                                    <span className="skill-badge pending">Chưa vào</span>
                                                </div>
                                            );
                                        }
                                        const bd = SKILL_STATUS_MAP[s.status] || SKILL_STATUS_MAP.pending;
                                        const pct = s.percent || 0;
                                        return (
                                            <div key={key} className="skill-row">
                                                <span className="skill-name">{s.label || SKILL_LABELS[key]}</span>
                                                <div className="skill-bar-wrap">
                                                    <div
                                                        className={`skill-bar-fill fill-${s.status}`}
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                                <span className="skill-count">
                                                    {s.total > 0 ? `${s.answered}/${s.total}` : '--'}
                                                    {s.score !== null && s.score !== undefined ? ` | ${s.score}đ` : ''}
                                                </span>
                                                <span className={`skill-badge ${bd.cls}`}>{bd.text}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ─── AUDIT LOG ─── */}
                            <div className="log-section">
                                <div className="log-title">Nhật ký hành vi (Audit Log)</div>
                                <div className="timeline">
                                    {logs.length === 0
                                        ? <div className="no-data" style={{ padding: '16px 20px', fontSize: '13px' }}>Chưa có nhật ký nào.</div>
                                        : logs.map((log, idx) => (
                                            <div key={idx} className={`timeline-item t-${log.loai_log}`}>
                                                <div
                                                    className="timeline-dot"
                                                    style={{ background: getLogDot(log.loai_log) }}
                                                />
                                                <div className="log-msg">{log.thong_bao}</div>
                                                <div className="log-sub">
                                                    {new Date(log.created_at).toLocaleTimeString('vi-VN')}
                                                    {log.ip_address && ` · ${log.ip_address}`}
                                                </div>
                                                {log.chi_tiet && (
                                                    <div className="log-highlight">{log.chi_tiet}</div>
                                                )}
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="empty-detail">
                            <i className="fa-solid fa-arrow-pointer" />
                            <p>Chọn một thí sinh để xem chi tiết</p>
                        </div>
                    )}
                </div>

            </div>

            {/* ─── MODAL: LẬP BIÊN BẢN ─── */}
            {showReportModal && (
                <div className="modal-overlay active" onClick={() => setShowReportModal(false)}>
                    <div className="modal-box modal-primary" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-title">
                                <i className="fa-solid fa-file-pen" /> Lập Biên Bản Vi Phạm
                            </div>
                            <button className="btn-close" onClick={() => setShowReportModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p className="student-info">
                                Thí sinh: <strong>{selectedStudent?.fullname} — {selectedStudent?.sbd}</strong>
                            </p>
                            <label className="form-label">Loại vi phạm</label>
                            <select
                                className="form-select"
                                value={reportData.violationType}
                                onChange={(e) => setReportData({ ...reportData, violationType: e.target.value })}
                            >
                                <option value="eye">Mắt không nhìn màn hình (Looking away)</option>
                                <option value="face">Không phát hiện khuôn mặt (No face)</option>
                                <option value="multiple">Phát hiện nhiều người (Multiple faces)</option>
                                <option value="audio">Phát hiện giọng nói lạ (Suspicious audio)</option>
                                <option value="phone">Sử dụng điện thoại (Phone detected)</option>
                                <option value="tab">Chuyển tab / rời màn hình</option>
                                <option value="other">Khác (Other)</option>
                            </select>
                            <label className="form-label" style={{ marginTop: 14 }}>Ghi chú giám thị</label>
                            <textarea
                                className="form-textarea"
                                placeholder="Mô tả chi tiết hành vi..."
                                value={reportData.notes}
                                onChange={(e) => setReportData({ ...reportData, notes: e.target.value })}
                            />
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowReportModal(false)}>Hủy bỏ</button>
                            <button className="btn-confirm" onClick={handleSaveReport} disabled={actionLoading}>
                                {actionLoading ? 'Đang lưu...' : 'Lưu biên bản'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── MODAL: KHÓA BÀI THI ─── */}
            {showLockModal && (
                <div className="modal-overlay active" onClick={() => setShowLockModal(false)}>
                    <div className="modal-box modal-danger" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-title">
                                <i className="fa-solid fa-triangle-exclamation" /> Đình Chỉ Bài Thi
                            </div>
                            <button className="btn-close" onClick={() => setShowLockModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="warning-box">
                                <strong>Cảnh báo:</strong> Hành động này sẽ khóa ngay quyền truy cập bài thi của thí sinh. Thí sinh không thể tự mở lại.
                            </div>
                            <p className="student-info" style={{ marginTop: 12 }}>
                                Thí sinh: <strong>{selectedStudent?.fullname} — {selectedStudent?.sbd}</strong>
                            </p>
                            <label className="form-label">Lý do đình chỉ</label>
                            <select
                                className="form-select"
                                value={lockData.reason}
                                onChange={(e) => setLockData({ ...lockData, reason: e.target.value })}
                            >
                                <option value="">-- Chọn lý do --</option>
                                <option value="cheat">Gian lận nghiêm trọng (Gross Cheating)</option>
                                <option value="identity">Sai lệch định danh (Identity mismatch)</option>
                                <option value="environment">Môi trường thi không đạt chuẩn</option>
                                <option value="admin">Quyết định của hội đồng thi</option>
                            </select>
                            <label className="form-label" style={{ marginTop: 14 }}>Thông báo gửi thí sinh</label>
                            <textarea
                                className="form-textarea"
                                value={lockData.message}
                                onChange={(e) => setLockData({ ...lockData, message: e.target.value })}
                            />
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowLockModal(false)}>Quay lại</button>
                            <button className="btn-confirm" onClick={handleLockStudent} disabled={actionLoading || !lockData.reason}>
                                {actionLoading ? 'Đang xử lý...' : 'Xác nhận KHÓA'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GiamSatThi;
